import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, ExternalLink, Lock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export const Route = createFileRoute('/brands/')({
  component: BrandsIndexPage,
});

function BrandsIndexPage() {
  return (
    <>
      <Helmet>
        <title>Brand Portfolio — AITOS Labs LLC</title>
        <meta
          name="description"
          content="The Print-on-Demand brand portfolio of AITOS Labs LLC. Active brands: Elzary and Avyra Atlas. Each brand runs on the AITOS in-house storefront and ops stack."
        />
        <link rel="canonical" href="https://aitos.app/brands" />
      </Helmet>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-purple-400 text-xs tracking-widest mb-4">BRAND PORTFOLIO</p>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">The AITOS Labs brand portfolio.</h1>
            <p className="text-gray-300 max-w-2xl mx-auto">
              AITOS Labs LLC is a holding company for Print-on-Demand brands. We build, own, and
              operate each brand end-to-end on our in-house storefront and ops stack.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Elzary */}
            <Link
              to="/brands/elzary"
              className="group bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-2xl overflow-hidden border border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="p-10 flex flex-col h-full">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                    Active
                  </span>
                </div>
                <h2 className="text-4xl font-bold mb-3">Elzary</h2>
                <p className="text-purple-300 text-lg mb-4">POD apparel & lifestyle</p>
                <p className="text-gray-300 mb-6 flex-grow">
                  Design pipeline, listing engine, fulfillment routing, CX, and growth — all run on
                  the AITOS in-house storefront and ops stack.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
                    Read case study
                    <ArrowRight size={18} />
                  </span>
                  <a
                    href="https://elzary.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    elzary.com
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </Link>

            {/* Avyra Atlas */}
            <Link
              to="/brands/avyraatlas"
              className="group bg-gradient-to-br from-pink-900/30 to-amber-900/20 rounded-2xl overflow-hidden border border-pink-500/30 hover:border-pink-500/60 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-2xl"
            >
              <div className="p-10 flex flex-col h-full">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                    Active
                  </span>
                </div>
                <h2 className="text-4xl font-bold mb-3">Avyra Atlas</h2>
                <p className="text-pink-300 text-lg mb-4">Print-on-Demand lifestyle</p>
                <p className="text-gray-300 mb-6 flex-grow">
                  Operated on the same AITOS in-house storefront stack as Elzary, with
                  multi-marketplace distribution and AI-assisted operations across design, listing,
                  fulfillment, and CX.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 text-white font-medium group-hover:gap-3 transition-all">
                    Read case study
                    <ArrowRight size={18} />
                  </span>
                  <a
                    href="https://avyraatlas.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    avyraatlas.com
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </Link>

            {/* Reserved slot */}
            <div className="bg-gray-900/50 rounded-2xl overflow-hidden border border-dashed border-gray-700 flex flex-col items-center justify-center text-center p-8">
              <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                <Lock size={24} className="text-gray-500" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-400">Next brand</h3>
              <p className="text-sm text-gray-500">Launching soon.</p>
            </div>
          </div>

          {/* CTA band */}
          <div className="bg-gray-900 rounded-3xl p-10 md:p-14 border border-gray-800 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Want AITOS to operate your brand?
            </h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              We can take over your POD operations end-to-end. Engagements start with a 2-week brand
              audit and an action plan.
            </p>
            <a
              href="/#cta?intent=audit"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity"
            >
              Book a brand audit
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
